import React from "react";
import {
  Box,
  Container,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  Heading,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

function SelensePrivacyPolicy() {
  return (
    <Box bg="gray.50" minH="100vh" py={{ base: 8, md: 12 }}>
      <Container
        maxW="3xl"
        bg="white"
        borderRadius="2xl"
        boxShadow="sm"
        p={{ base: 6, md: 10 }}
      >
        <Box mb={6}>
          <Text fontSize="sm" fontWeight="700" letterSpacing="wide" color="teal.600">
            SELENSE AI
          </Text>
          <Heading as="h1" size="lg" mt={2} color="gray.800">
            Privacy Policy
          </Heading>
        </Box>

        <Text fontSize="sm" color="gray.600">
          Effective date: 12 October 2026
        </Text>
        <Text fontSize="sm" color="gray.600" mb={6}>
          Last updated: 12 October 2026
        </Text>

        <Text mb={4} color="gray.700" lineHeight="1.8">
          Selense AI is a care-support product developed by Serena Space and is
          currently available to selected pilot users. This policy applies to
          Selense AI only. It is separate from the privacy policy for the wider
          Serena Space website. The legal entity responsible for operating
          Selense has not been confirmed in the product information available
          for this policy.
        </Text>

        <Divider mb={8} />

        <Stack spacing={7}>
          <Box>
            <Heading as="h2" size="md" mb={3} color="teal.700">
              Information we process and why
            </Heading>
            <Text color="gray.700" lineHeight="1.8">
              Firebase Authentication manages sign-in. Selense processes
              account and sign-in information, information about a CareCircle
              and the person receiving care, and the information you add to the
              service. This can include messages, notes, health details,
              medications, health events, vital readings, and saved memories.
              These records are associated with the signed-in account.
              Technical request information may also be generated when the
              service is used.
            </Text>
            <Text color="gray.700" lineHeight="1.8" mt={3}>
              Care information can be sensitive and may describe someone other
              than the account holder. Please share only information relevant to
              using Selense and avoid unnecessary identifying details.
            </Text>
            <Text color="gray.700" lineHeight="1.8" mt={3}>
              We use this information to authenticate accounts, manage CareCircles
              and care records, provide AI-assisted responses and continuity
              between conversations, operate and secure the service, and assess
              the usefulness and reliability of the pilot.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={3} color="teal.700">
              AI and service providers
            </Heading>
            <Text color="gray.700" lineHeight="1.8">
              To generate a response, Selense sends your message, relevant
              conversation history, and care context to the AI provider
              configured for the service. The implementation uses OpenAI by
              default and can be configured to use Google Gemini. These
              providers process information to return AI-generated responses.
              Their retention, use of data to improve or train models, and
              processing locations have not been confirmed for this service.
            </Text>
            <Text color="gray.700" lineHeight="1.8" mt={3}>
              Selense also uses Firebase Authentication for sign-in, MongoDB to
              store care records, AWS to host the frontend, and Google Cloud Run
              to run the backend. Information is processed by these services as
              needed to provide Selense. Frontend and backend hosting locations
              do not establish where MongoDB, Firebase, or AI providers store or
              process information.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={3} color="teal.700">
              AI limitations and health safety
            </Heading>
            <Text color="gray.700" lineHeight="1.8" fontWeight="600">
              Selense AI does not diagnose, prescribe, or replace a qualified
              healthcare professional.
            </Text>
            <Text color="gray.700" lineHeight="1.8" mt={3}>
              AI-generated information may be incomplete, inaccurate, or
              unsuitable for a particular person. Do not rely on Selense for
              diagnosis, treatment, or medication decisions, or delay care
              because of a response. Seek advice from a qualified professional;
              for urgent medical concerns, contact an appropriate emergency or
              care service.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={3} color="teal.700">
              Storage, security, and your choices
            </Heading>
            <Text color="gray.700" lineHeight="1.8">
              The backend checks the signed-in account and its ownership of care
              records before allowing access. This describes the access checks
              in the application; it does not establish encryption or the
              security controls used by every service provider.
            </Text>
            <Text color="gray.700" lineHeight="1.8" mt={3}>
              Use <strong>Delete Selense data</strong> in your signed-in Selense
              experience to delete your Selense health records. This does not
              delete your Serena Space sign-in or information used by other
              Serena Space services. To ask us to correct information or discuss
              account closure or other applicable privacy rights, contact us.
              We will consider requests under applicable law and explain any
              relevant limits. The implementation does not establish a fixed
              retention period or deletion timing for provider or backup
              copies. If you stop using Selense, do not assume that information
              is automatically deleted.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mb={3} color="teal.700">
              Contact
            </Heading>
            <Text color="gray.700" lineHeight="1.8">
              For privacy questions, concerns, or requests about your
              information, contact Serena Space at{" "}
              <Link href="mailto:support@serenaspace.com" color="teal.600">
                support@serenaspace.com
              </Link>
              .
            </Text>
          </Box>

          <Box id="faq">
            <Heading as="h2" size="md" mb={3} color="teal.700">
              Frequently asked questions
            </Heading>
            <Accordion allowToggle>
              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Who operates Selense?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  Selense AI is developed by Serena Space. The legal entity
                  responsible for operating Selense has not been confirmed in
                  the product information available for this policy.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    What information does Selense store?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  Selense stores sign-in details through Firebase Authentication
                  and care records in its database. Care records can include
                  CareCircle details, messages, notes, health information,
                  medications, events, readings, and saved memories.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Who can access my health information?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  The application checks the signed-in account and associates
                  care records with its Firebase account ID. The services that
                  help operate Selense, including its database and AI provider,
                  process information as needed to provide their services.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    How does Selense use AI?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  Selense sends your message, relevant conversation history,
                  and care context to the configured AI provider to generate a
                  response. The service uses OpenAI by default and can be
                  configured to use Google Gemini. Provider retention and model
                  training terms for Selense have not been confirmed.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Can I edit or delete my information?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  Selense does not currently provide a control to edit every
                  saved record. Contact support to request a correction. Use
                  <strong> Delete Selense data</strong> in the signed-in
                  experience to remove Selense health records.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Can I delete my account?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  Yes.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Is Selense a replacement for medical advice?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel color="gray.700">
                  No. Selense AI does not diagnose, prescribe, or replace a
                  qualified healthcare professional. AI responses may be
                  inaccurate or incomplete; seek professional care for medical
                  decisions and urgent concerns.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </Stack>

        <Box mt={8}>
          <Link as={RouterLink} to="/" color="teal.600" fontWeight="600">
            Back to Selense
          </Link>
        </Box>
      </Container>
    </Box>
  );
}

export default SelensePrivacyPolicy;
